import {type I18n} from '@lingui/core'
import {msg} from '@lingui/macro'
import {nanoid} from 'nanoid/non-secure'

import {AbortError} from '#/lib/async/cancelable'
import {ServerError} from '#/lib/media/video/errors'
import {type CompressedVideo} from '#/lib/media/video/types'
import {logger} from '#/logger'

// AWS S3 Configuration - can be set via environment variables
const AWS_S3_BUCKET = process.env.EXPO_PUBLIC_AWS_S3_BUCKET || ''
const AWS_S3_REGION = process.env.EXPO_PUBLIC_AWS_S3_REGION || 'us-east-1'
// Backend endpoint to generate presigned URLs (recommended for security)
// If not set, will attempt direct upload with credentials (not recommended for production)
const PRESIGNED_URL_ENDPOINT = process.env.EXPO_PUBLIC_S3_PRESIGNED_URL_ENDPOINT || ''

// Threshold for using S3 direct upload (90MB)
// Set below PDS limit (100MB) to prevent hitting PDS limits and causing network errors
export const S3_UPLOAD_THRESHOLD = 90 * 1024 * 1024 // 90MB

/**
 * Uploads a video directly to AWS S3 with progress tracking
 * Returns the public URL of the uploaded video
 */
export async function uploadVideoToS3({
  video,
  setProgress,
  signal,
  _,
}: {
  video: CompressedVideo
  setProgress: (progress: number) => void
  signal: AbortSignal
  _: I18n['_']
}): Promise<string> {
  if (signal.aborted) {
    throw new AbortError()
  }

  // Validate AWS configuration
  if (!AWS_S3_BUCKET) {
    throw new ServerError(
      _(
        msg`AWS S3 bucket not configured. Please set EXPO_PUBLIC_AWS_S3_BUCKET environment variable.`,
      ),
    )
  }

  // Require presigned URL endpoint for security (credentials should never be in client code)
  if (!PRESIGNED_URL_ENDPOINT) {
    throw new ServerError(
      _(
        msg`S3 presigned URL endpoint not configured. Please set EXPO_PUBLIC_S3_PRESIGNED_URL_ENDPOINT environment variable. This endpoint should generate presigned URLs for secure S3 uploads.`,
      ),
    )
  }

  // Get the video blob
  let blob: Blob
  if (video.bytes) {
    blob = new Blob([video.bytes], {type: video.mimeType})
  } else if (video.uri.startsWith('blob:')) {
    const response = await fetch(video.uri)
    if (!response.ok) {
      throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`)
    }
    blob = await response.blob()
  } else if (video.uri.startsWith('data:')) {
    const response = await fetch(video.uri)
    blob = await response.blob()
  } else {
    throw new ServerError(_(msg`Unsupported video URI type for S3 upload`))
  }

  if (signal.aborted) {
    throw new AbortError()
  }

  // Generate a unique key for the video
  const ext = video.mimeType.split('/')[1] || 'mp4'
  const key = `videos/${nanoid(12)}.${ext}`

  // Get presigned URL from backend endpoint
  let uploadUrl: string
  let uploadMethod: string
  let uploadHeaders: Record<string, string> = {}

  try {
    const response = await fetch(PRESIGNED_URL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key,
        contentType: video.mimeType,
        fileSize: blob.size,
      }),
    })

    if (!response.ok) {
      throw new Error(`Failed to get presigned URL: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    uploadUrl = data.url
    uploadMethod = data.method || 'PUT'
    uploadHeaders = data.headers || {}
    
    console.log('✅ Got presigned URL from backend', {
      url: uploadUrl.substring(0, 100) + '...',
      method: uploadMethod,
    })
  } catch (e) {
    const errorMsg = e instanceof Error ? e.message : String(e)
    console.error('❌ Failed to get presigned URL', {error: errorMsg})
    logger.error('Failed to get presigned URL', {error: e})
    throw new ServerError(_(msg`Failed to get upload URL: ${errorMsg}`))
  }

  // Use XMLHttpRequest for progress tracking
  const xhr = new XMLHttpRequest()
  xhr.timeout = 12 * 60 * 60 * 1000 // 12 hours

  console.log('🚀 Starting S3 direct upload', {
    bucket: AWS_S3_BUCKET,
    region: AWS_S3_REGION,
    key,
    size: blob.size,
    mimeType: video.mimeType,
  })
  logger.debug('Starting S3 direct upload', {
    bucket: AWS_S3_BUCKET,
    region: AWS_S3_REGION,
    key,
    size: blob.size,
  })

  // For now, we'll use a simple PUT request
  // In production, you should use presigned URLs from your backend
  const uploadResult = await new Promise<string>((resolve, reject) => {
    xhr.upload.addEventListener('progress', e => {
      if (e.lengthComputable) {
        const progress = e.loaded / e.total
        setProgress(progress)
      }
    })

    xhr.onloadend = () => {
      if (signal.aborted) {
        reject(new AbortError())
        return
      }
      if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
        // Upload successful, return the public URL
        // If using presigned URL, the response might contain the URL, otherwise construct it
        let publicUrl: string
        try {
          const responseData = xhr.responseText ? JSON.parse(xhr.responseText) : null
          publicUrl = responseData?.url || `https://${AWS_S3_BUCKET}.s3.${AWS_S3_REGION}.amazonaws.com/${key}`
        } catch {
          publicUrl = `https://${AWS_S3_BUCKET}.s3.${AWS_S3_REGION}.amazonaws.com/${key}`
        }
        console.log('✅ S3 upload successful', {url: publicUrl})
        resolve(publicUrl)
      } else {
        const errorDetails = xhr.responseText || xhr.statusText
        console.error('❌ S3 upload failed', {
          status: xhr.status,
          statusText: xhr.statusText,
          errorDetails: errorDetails.substring(0, 200),
        })
        logger.error('S3 upload failed', {
          status: xhr.status,
          statusText: xhr.statusText,
          errorDetails: errorDetails.substring(0, 200),
        })
        reject(
          new ServerError(
            _(msg`S3 upload failed with status ${xhr.status}: ${xhr.statusText}`),
          ),
        )
      }
    }

    xhr.onerror = () => {
      console.error('❌ S3 upload network error')
      logger.error('S3 upload network error')
      reject(new ServerError(_(msg`Network error during S3 upload`)))
    }

    xhr.ontimeout = () => {
      console.error('❌ S3 upload timeout')
      logger.error('S3 upload timeout')
      reject(new ServerError(_(msg`S3 upload timeout`)))
    }

    xhr.open(uploadMethod, uploadUrl)
    // Set headers from presigned URL response or direct upload
    Object.entries(uploadHeaders).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })
    // Always set Content-Type if not already set
    if (!uploadHeaders['Content-Type']) {
      xhr.setRequestHeader('Content-Type', video.mimeType)
    }
    xhr.send(blob)
  })

  return uploadResult
}
